import { useState } from 'react';
import { Text, View } from 'react-native';
import { Icon } from '../index';
import { useTranslation } from 'react-i18next';

import type { DraftSet, WeightUnit } from './state/loggerDraft';
import type { useTheme } from './theme';
import {
  axisHasLoadValue,
  numericFieldErrorText,
  type IntensityKind,
} from '@heraion/shared';
import { steppedWeight } from './weightStepper';
import { useDockedLoadBar } from './LoggerDock';
import { useFieldFocusReporter } from './useFieldFocus';
import { useRevealRowRef } from './LoggerRevealScroller';
import { SetRowStepperBar } from './SetRowStepperBar';
import { FocusPanelCardio } from './FocusPanelCardio';
import { FocusPanelTimed } from './FocusPanelTimed';
import { FocusPanelRepsField } from './FocusPanelRepsField';
import { HeaderTooltip } from './HeaderTooltip';
import { makeFocusPanelInputsStyles } from './focusPanelInputsStyles';
import { amrapNeedsReps } from './amrapGate';
import { STEPS_KG, STEPS_LB, STEPS_PERCENT } from './weightStepConstants';
import { unitWord } from '../weightUnitLabels';
import { ExtraLoadField } from './ExtraLoadField';

// The DraftSet fields the input area writes through. Mirrors the strength
// inputs + the cardio duration/optionals (parity with CardioSetRow).
export type EditableSetField =
  | 'reps'
  | 'weight'
  | 'extraNegative'
  | 'rir'
  | 'rpe'
  | 'duration'
  | 'distanceM'
  | 'avgHrBpm'
  | 'calories'
  | 'dropset'
  | 'side';

// FocusPanelInputs (timeline logger) — the reps/weight (or cardio) data-entry
// area of the FocusPanel, extracted to keep FocusPanel under the 500-line
// budget. It owns the focus state for the contextual stepper bar and writes
// through `onChange` on every keystroke, exactly as it did inline. Rendered
// only when a set is bound; the dropset/myorep case defers its rounds to the
// DropsetBlock in FocusPanelDetails (the inline cells hide here, mirroring
// the card logger). Effort left this row in AP-0b: the meter under the card
// (FocusPanelEffort) is the single effort write path now.
export function FocusPanelInputs({
  set: s,
  isBodyweight,
  isCardio,
  isTimeBased,
  defaultUnit,
  prescribeLoad,
  onChange,
  onOpenPlates,
  onToggleUnit,
  onWeightFocus,
  theme,
}: {
  set: DraftSet;
  isBodyweight: boolean;
  isCardio: boolean;
  /** isSetTimed(entry, set): a hold, or a set done for time (#41); shows the
   *  duration and the added load only. */
  isTimeBased: boolean;
  defaultUnit: WeightUnit;
  /** PE-34 — PRESCRIBE ONLY (undefined in the logger). Present means the
   *  weight field is authoring the PRESCRIBED load, not recording a lift:
   *  `kind` is the row's load axis and `cellUnit` the unit the draft's cells
   *  are expressed in. Two things follow, and both are the difference between
   *  a field and a trap:
   *   - the label follows the AXIS — "%" on percent-of-max, or the cell's own
   *     unit — because a field labelled kg holding 70 percent lies;
   *   - on an axis that carries NO number (history / sforzo / none) the field
   *     is not rendered at all, instead of accepting a value the kind ignores.
   *  The unit chip and the plate calculator go with it (PE-35): both act on a
   *  measured lift, and there is no lift here yet. */
  prescribeLoad?: {
    kind: IntensityKind;
    cellUnit: WeightUnit;
    /** The axis's own short label ("storico", "sforzo"…), resolved by the
     *  caller through useKindLabels — the ONE label resolver, which lives in
     *  ProgramComposer and must not be imported down into the Logger. Shown
     *  in place of the field on an axis that carries no number. */
    axisLabel: string;
  };
  onChange: (patch: Partial<Pick<DraftSet, EditableSetField>>) => void;
  onOpenPlates: () => void;
  onToggleUnit: () => void;
  /** Fired when the weight field gains focus — FocusPanel uses it for the
   *  once-per-exercise per-implement tip («enter ONE dumbbell's weight»).
   *  Kept as a callback so this component stays free of tip bookkeeping
   *  (useTransientTip lives once at FocusPanel level). */
  onWeightFocus?: () => void;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  const styles = makeFocusPanelInputsStyles(theme);

  // ⛔ LG-59 — the provenance of a DERIVED weight does NOT live on this
  // label. It was put here on 1/09 (a glyph plus a swapped bubble) and taken
  // straight back off on 2/09, his words: «non ha senso mettere lì una cosa
  // che va altrove e che ha già il suo posto, quel label deve essere solo
  // KG/LB con il suo hint». It lives in the history widget's first section
  // now (`weightCandidateRows.ts`), where the athlete goes to look things up
  // — and where it can show the instruments this cell did NOT use.
  //
  // ⚠️ The second defect that ruling settled: one Pressable can only carry
  // one bubble, so a proposal tip here did not sit BESIDE the kg/lb
  // explanation, it REPLACED it. Do not re-add a second meaning to this
  // label without solving that first.

  // PE-34 — does this axis take a typed number at all? ⛔ The shared
  // predicate, never a fourth hand-written copy: the dispatch and the
  // hydration ask the same question and the three answers must agree.
  const loadAxisHasValue = axisHasLoadValue(prescribeLoad);
  // DS-83: the unit word comes from `unitWord` (CN-26's single source for
  // "how this app spells kg/lb"), not a per-callsite i18n key: that drift is
  // what left "LB" latin on the dock beside "ФУНТ" cyrillic here.
  const weightUnitLabel =
    prescribeLoad?.kind === 'percent-of-max'
      ? t('mobile.logger.setPercentOfMax', { defaultValue: '%' })
      : unitWord(t, prescribeLoad?.cellUnit ?? defaultUnit).toUpperCase();

  // Serve SOLO fuori dal dock: vedi il commento su `inFlow` più sotto.
  const [weightFocused, setWeightFocused] = useState(false);
  const [stepSign, setStepSign] = useState<1 | -1>(1);

  const applyDelta = (delta: number) => {
    onChange({ weight: steppedWeight(s.weight, delta) });
  };

  // Tell the logger a field is holding the keyboard, so the rest-end resume
  // waits instead of pulling the caret out of a half-typed number (TM-18a).
  const fieldFocus = useFieldFocusReporter();

  // Inline numeric validation (2026-06-08), locale-aware; danger border on the
  // bad field + one message line below the input row.
  const repsErr = numericFieldErrorText('reps', s.reps, t);
  const weightErr = numericFieldErrorText('weight', s.weight, t);
  // A prescribed AMRAP starts with an empty reps cell (LG-28) and cannot be
  // completed until it is filled (useTimelineLogger.amrapNeedsReps). That rule
  // has to be READABLE, or the band tap just stops working for no visible
  // reason. It is not an error — nothing is wrong yet, the athlete simply has
  // not typed the result — so it renders as the row's cue and never turns the
  // field's border red. `validateNumericField` deliberately calls an empty
  // field valid (it must, or every field would flash mid-typing), so this
  // state cannot come from there and is computed here.
  const needsReps = amrapNeedsReps(s);
  const rowError = [repsErr, weightErr].filter(Boolean).join(' · ') || null;
  const rowCue =
    rowError === null && needsReps
      ? t('mobile.logger.amrapNeedsReps', {
          defaultValue: 'Type the reps you did to log this set.',
        })
      : null;

  // ⭐ LG-94 — the weight controls are DOCKED, not rendered here. Rendered in
  // the flow they were born exactly where the keyboard covers them, so no
  // state of the app existed in which an athlete could see them: whoever did
  // not already know they were there never would.
  //
  // ⛔ The bar is still built HERE, with this panel's own derivations, and only
  // handed over to be drawn. The dock does not know what a step table or a
  // prescribed load is, and giving it that knowledge would put every condition
  // below in a second place able to disagree with this one.
  //
  // ⭐⭐ LG-95 — la banda è PERMANENTE, non più legata al fuoco del campo peso.
  // Nata e morta col fuoco faceva due danni: il rest timer, che nel dock le sta
  // SOPRA, saltava su a ogni tap e ricadeva al blur (il dock cresce verso
  // l'alto, quindi a muoversi è sempre l'altro); e quando `Keyboard.dismiss()`
  // non consegna il blur — input già null perché il modale del calcolatore ha
  // preso il posto, o il campo è stato smontato sotto uno swap di card
  // (`useFieldFocus.ts:33-50`) — la banda restava agganciata al fondo a
  // tastiera chiusa, lontana dalla card. Sua parola del 14/09, che ribalta la
  // clausola «zero spazio quando non serve» di LG-94.
  //
  // ⛔ La condizione NON è nuova: è la stessa di prima meno `weightFocused`.
  // «Questo set ha un asse di carico editabile qui» era già scritta.
  const loadBar =
    loadAxisHasValue && !isCardio && !isTimeBased && !s.dropset ? (
      <SetRowStepperBar
        unit={s.unit}
        // ⭐ LG-37b fetta D — le righe a corpo libero ARRIVANO al
        // calcolatore. La soppressione `isBodyweight ||` è caduta qui:
        // `PlateCalcModal` ha una modalità bodyweight completa e viva dal
        // giorno in cui è nata — il segno ± fra zavorra e assistenza, il
        // sunto che non parla di coppie — e nessuno poteva raggiungerla.
        // Era una capacità finita, tradotta in cinque lingue, e chiusa a
        // chiave (`timeline-logger.md §LG-37b`, punto 3).
        // ⛔ `prescribeLoad` RESTA nella condizione, e non per simmetria: una
        // riga prescritta non ha un peso che l'atleta possa comporre, il
        // numero lo scrive il programma. Toccare quel ramo sarebbe stata una
        // seconda fetta travestita da parentesi.
        onOpenPlates={prescribeLoad ? undefined : onOpenPlates}
        onToggleUnit={prescribeLoad ? undefined : onToggleUnit}
        stepSign={stepSign}
        setStepSign={setStepSign}
        // A percentage does not step by plate sizes. 0.5/1.25/2.5 kg are the
        // increments a barbell can actually make; on %MAX the useful moves
        // are whole points (a11y audit, functional).
        stepMagnitudes={
          prescribeLoad?.kind === 'percent-of-max'
            ? STEPS_PERCENT
            : s.unit === 'lb'
              ? STEPS_LB
              : STEPS_KG
        }
        applyDelta={applyDelta}
        theme={theme}
      />
    ) : null;
  // ⛔ False in the program editor, which mounts this same panel inside a
  // swiped week deck with no screen bottom to dock to. There the bar stays
  // exactly where it has always been.
  //
  // ⭐ The band is put on ONLY for the dock. In the flow the bar sits inside
  // the card that already frames it; out here it would otherwise be a run of
  // buttons floating over the page with no edge and no name.
  // ⛔ IL FUORI-DOCK RESTA COM'ERA. `useDockedLoadBar` torna `inFlow` non-null
  // solo dove NON c'è un dock: è il program editor, che monta questo stesso
  // pannello nel mazzo di settimane sfogliabile, senza rest timer sopra e
  // senza tastiera che copra la barra. La decisione del 14/09 parla del DOCK e
  // dei suoi due difetti; renderla permanente anche là sarebbe una superficie
  // in più che lui non ha chiesto.
  const { inFlow } = useDockedLoadBar(loadBar, theme);
  const revealRowRef = useRevealRowRef();

  return (
    <>
      {/* LG-98 — THIS is the row the dock was covering. The measuring and the
          scrolling live in LoggerRevealScroller; here it only says which row. */}
      <View style={styles.inputRow} ref={revealRowRef}>
        {isTimeBased ? (
          <FocusPanelTimed
            set={s}
            onChange={onChange}
            theme={theme}
          />
        ) : isCardio ? (
          <FocusPanelCardio
            set={s}
            onChange={onChange}
            theme={theme}
          />
        ) : s.dropset ? (
          // Dropset/myorep active: the chain rounds own input below (the
          // DropsetBlock in FocusPanelDetails). Hide the single reps/weight
          // row to avoid a second, conflicting parent input — mirrors the
          // card logger, where the type bar replaces the inline cells.
          <Text style={styles.hint}>
            {t('mobile.timeline.dropsetActive', {
              defaultValue: 'Dropset: edit the steps below.',
            })}
          </Text>
        ) : (
          <>
            <FocusPanelRepsField
              set={s}
              invalid={repsErr !== null}
              onChange={onChange}
              fieldFocus={fieldFocus}
              styles={styles}
              theme={theme}
            />
            {/* PE-34 — on history / sforzo / none the axis carries no number,
                so there is no field: an editable cell whose value the kind
                ignores is the swallowing input this slice removes.
                ⭐ The cell NAMES the axis instead of going blank — a control
                that vanishes with no explanation reads as a bug, and the
                selector that would explain it sits BELOW the row, so a
                top-down scan meets the effect before the cause (a11y audit).
                ⛔ Not a new idiom: ComposerSetRow's valueless branch already
                prints the kind label in the value cell. */}
            {!loadAxisHasValue ? (
              // ⭐ Banco visivo, 30/08: la prima forma metteva il nome dell'asse
              // all'altezza del VALORE e la colonna sembrava aver perso la sua
              // intestazione, mentre reps accanto ne aveva una. L'asse sta dove
              // sta l'etichetta, e sotto c'è il trattino che questa card usa già
              // per «qui non c'è un numero» — senza bordo, così non si legge
              // come un campo disabilitato.
              <View style={styles.field}>
                <Text style={styles.fieldLabel} numberOfLines={1}>
                  {prescribeLoad?.axisLabel ?? ''}
                </Text>
                <Text style={styles.axisOnlyText}>—</Text>
              </View>
            ) : (
            <View style={styles.field}>
              {/* Weight label carries the SAME tap-tip as the card logger's
                  weight column (HeaderTooltip, slice 5b) — explains kg/lb +
                  the ± assist convention for bodyweight rows. Reuses the
                  existing setWeightTip / setExtraTip keys; respects the Hints
                  toggle (off ⇒ plain label). Left-aligned to match the reps /
                  effort labels via the cellStyle override. */}
              {/* «2×» on the label when the set counts both sides (UNI /
                  two-implement): the convention — enter ONE implement's
                  weight — is readable right where the number is typed,
                  at zero layout cost (IB-8 family). Words-only a11yLabel:
                  the raw × glyph is never vocalised (audit 2026-07-14);
                  numberOfLines pins the row rhythm on wide locales (ФУНТ). */}
              <HeaderTooltip
                label={`${s.loadedSides === 2 ? '2× ' : ''}${
                  isBodyweight ? t('mobile.logger.setExtra') : weightUnitLabel
                }`}
                a11yLabel={
                  [
                    s.loadedSides === 2
                      ? t('mobile.logger.setWeightBothSidesA11y', {
                          unit: s.unit,
                          defaultValue:
                            'Weight of one side, both sides counted ({{unit}})',
                        })
                      : prescribeLoad?.kind === 'percent-of-max'
                        ? // "%" is a glyph, and a screen reader saying
                          // "percent" alone does not say percent OF WHAT.
                          // KG/LB read as words already; this one needed
                          // spelling out.
                          t('mobile.logger.setPercentOfMaxA11y', {
                            defaultValue: 'Percent of max',
                          })
                        : undefined,
                  ]
                    .filter(Boolean)
                    .join('. ') || undefined
                }
                labelNumberOfLines={1}
                tip={
                  isBodyweight
                    ? t('mobile.logger.setExtraTip', {
                        defaultValue:
                          'Bodyweight assistance or added load. Tap «Assisted» for band/machine assist (the field shows a − and turns amber); a belt/plate add is positive.',
                      })
                    : prescribeLoad
                      ? t('mobile.logger.prescribedLoadTip', {
                          defaultValue:
                            "The load you prescribe, in the program's unit: kilos or a % of max. Choose the unit just below.",
                        })
                      : t('mobile.logger.setWeightTip', {
                          defaultValue:
                            'Weight lifted per set. Switch kg/lb with the unit chip on each row.',
                        })
                }
                labelStyle={styles.fieldLabel}
                cellStyle={styles.labelCell}
                theme={theme}
              />
              <ExtraLoadField
                set={s}
                isBodyweight={isBodyweight}
                invalid={weightErr !== null}
                onChange={onChange}
                fieldFocus={fieldFocus}
                onFocusChange={setWeightFocused}
                onWeightFocus={onWeightFocus}
                styles={styles}
                theme={theme}
              />
            </View>
            )}
            {/* Empty third cell: keeps reps + weight each a 2-unit cell of the
                6-unit card grid, aligned with the rows below. The effort value
                moved to the meter (FocusPanelEffort, AP-0b). */}
            <View style={styles.field} />
          </>
        )}
      </View>

      {rowError ? (
        <View style={styles.fieldError} accessibilityRole="alert">
          <Icon name="warn" size={theme.typography.sizes.xs} color={theme.palette.danger} />
          <Text style={styles.fieldErrorText}>{rowError}</Text>
        </View>
      ) : rowCue ? (
        // Same slot, different voice: no warn glyph and no danger tint, because
        // an unfilled AMRAP is the normal state of a set you have not done yet.
        // It carries a text sentence, so it never depends on colour.
        <View style={styles.fieldError}>
          <Text style={styles.fieldCueText}>{rowCue}</Text>
        </View>
      ) : null}

      {weightFocused ? inFlow : null}
    </>
  );
}
